import React, { Fragment, FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { useEffect, useState } from "preact/hooks";

import cx from "classnames";
import Cookies from "js-cookie";

import fauxDropdown from "../../modules/faux-dropdown";
import loginButton from "../../modules/login-button";
import modal from "../../modules/modal";
import utils from "../../helpers/utils";
import Message from "./components/Message"; // eslint-disable-line @typescript-eslint/no-unused-vars

interface IAppProps {
  apiUrl: string;
  inNav: boolean;
  hasRole: boolean;
  loginLink?: string;
  loginLabel?: string;
  loginToLabel?: string;
  loginToLink?: string;
  loginToTarget?: string;
  changeLoginLink?: string;
  changeLoginLabel?: string;
  multipleLogin?: boolean;
  disabledPortals: any[] | null;
  forgetLogins?: boolean;
  forgetLoginsFrom?: string;
  roleRegionEnabled: boolean;
  occLoginText?: string;
  occLoginLink?: string;
  occLoginButtonTarget?: string;
  occTarget?: string;
  occModalId?: string;
  occEnabled?: string;
  occTimeout?: string;
}

const translateMessageObject = ({
  MessageId,
  Title,
  MessageText,
  MessageType,
  CtaLinkText,
  CtaLinkUrl,
  LastUpdatedOn,
  Dismissible,
  Order,
  OpenInNewTab,
}) => {
  return {
    id: MessageId,
    title: Title,
    text: MessageText,
    type: MessageType,
    linkText: CtaLinkText,
    linkUrl: CtaLinkUrl,
    lastUpdatedOn: LastUpdatedOn,
    dismissible: Dismissible,
    order: Order,
    openInNewTab: OpenInNewTab,
  };
};

const App: FunctionComponent<IAppProps> = ({
  apiUrl,
  inNav,
  hasRole,
  loginLink,
  loginLabel,
  loginToLabel,
  loginToLink,
  loginToTarget,
  changeLoginLink,
  changeLoginLabel,
  multipleLogin,
  disabledPortals,
  forgetLogins,
  forgetLoginsFrom,
  roleRegionEnabled,
  occLoginText,
  occLoginLink,
  occLoginButtonTarget,
  occTarget,
  occModalId,
  occEnabled,
  occTimeout,
}) => {
  const [allMessages, setAllMessages] = useState([]); // Keep the whole list for when a message is dismissed to show next available message
  const [messages, setMessages] = useState([]); // List of currently showing messages
  const [loaded, setLoaded] = useState(false);
  const cookieName = "MessagesDismissed";
  const getMsgCookie = () => {
    return Cookies.get(cookieName) ? JSON.parse(Cookies.get(cookieName)) : [];
  };
  const updateMsgCookie = (dismissed) => {
    return Cookies.set(cookieName, JSON.stringify(dismissed), {
      expires: 360,
      path: "/",
      secure: true,
    });
  };

  useEffect(() => {
    fetch(apiUrl)
      .then((res) => res.json())
      .then((res) => {
        const msg = res.sort(utils.compareNumbers).map((m) => translateMessageObject(m));
        const messagesDismissed = getMsgCookie();
        setAllMessages(msg);
        if (messagesDismissed.length) {
          // Remove any messages that have been dismissed & not been updated since (don't show these to the user)
          const messagesToShow = msg.filter((m) => {
            return !messagesDismissed.find((dism) => dism.id === m.id && dism.lastUpdatedOn === m.lastUpdatedOn);
          });
          // Remove any messages from the cookie that have changed in the CMS since - these need to be shown again
          const newDismissed = messagesDismissed.filter((dism) => {
            return msg.find((m) => m.id === dism.id && m.lastUpdatedOn === dism.lastUpdatedOn);
          });
          if (newDismissed.length || newDismissed.length !== messagesDismissed.length) {
            updateMsgCookie(newDismissed);
          }
          setMessages(messagesToShow.slice(0, 2));
        } else {
          setMessages(msg.slice(0, 2));
        }
        setLoaded(true);
      })
      .catch((err) => console.log("error fetching or showing messages data: ", err));
  }, [hasRole]);

  useEffect(() => {
    if (forgetLogins && forgetLoginsFrom) {
      const forgetLoginsCookie = Cookies.get("DoNotForgetLoginAgain");
      const ignoreCookie = forgetLoginsFrom !== forgetLoginsCookie;
      // Check for do not forget cookie if exists / ignore this
      if (!forgetLoginsCookie || ignoreCookie) {
        // Or forget the login cookies & set a do not forget cookie so that next time the login will be remembered
        var cookiesArray = document.cookie?.split(";");
        cookiesArray.forEach((element) => {
          var cookieName = element?.split("=")[0];
          if (
            cookieName.includes("-LoginTitle") ||
            cookieName.includes("-LoginLink") ||
            cookieName.includes("-LoginLinkTarget")
          ) {
            Cookies.remove(cookieName.trim());
          }
        });

        Cookies.set("DoNotForgetLoginAgain", forgetLoginsFrom, {
          expires: 45,
          path: "/",
          secure: true,
        });
      }
    }
    if (loaded) {
      setTimeout(() => {
        fauxDropdown.init();
        modal.init();
        loginButton.init();
      });
    }
  }, [forgetLogins, loaded]);

  // If no role selected and role/regions enabled, don't bother. First direct the user to select the role (not sure this will ever actually happen anymore)
  if (!hasRole && roleRegionEnabled) {
    return (
      <button
        className={cx("button button--primary button--arrow js-modal-trigger", {
          "nav__mobile-section-button": inNav,
        })}
        data-target="#modal-role"
      >
        {loginLabel}
      </button>
    );
  }

  const dropdownClass = inNav
    ? "faux-dropdown--relative faux-dropdown--centered"
    : "faux-dropdown--in-hat faux-dropdown--sm";

  const renderLinks = () => {
    const disabledTo = portalDisabled(loginToLink);
    const disabledChange = portalDisabled(changeLoginLink);

    return multipleLogin && loginToLink ? (
      <Fragment>
        <a
          className={cx(
            "button button--primary button--arrow faux-dropdown__cta js-login-top-hat js-login-hat-button",
            disabledTo
          )}
          href={loginToLink}
          target={loginToTarget}
          data-occ-Login-Text={occLoginText}
          data-occ-Login-Link={occLoginLink}
          data-occ-Login-Button-Target={occLoginButtonTarget}
          data-occ-Target={occTarget}
          data-occ-Modal-Id={occModalId}
          data-occ-Enabled={occEnabled}
          data-occ-Timeout={occTimeout}
        >
          {loginToLabel || loginLabel}
        </a>
        <a
          className={cx("faux-dropdown__link js-change-login-link", disabledChange)}
          href={changeLoginLink}
        >
          {changeLoginLabel}
        </a>
      </Fragment>
    ) : (
      <Fragment>
        {renderSingleLoginButton("faux-dropdown__cta")}
        <a
          className={cx("faux-dropdown__link hidden", disabledChange)}
          href={changeLoginLink}
        >
          {changeLoginLabel}
        </a>
      </Fragment>
    );
  };

  const portalDisabled = (link?: string) => {
    return disabledPortals ? (disabledPortals.find((dlink) => dlink === link) ? "disabled" : "") : null;
  };

  const renderSingleLoginButton = (className?: string) => {
    const disabled = portalDisabled(loginLink);
    return (
      <a
        className={cx("button button--primary button--arrow faux-dropdown__cta", className, disabled)}
        href={loginLink}
        target={multipleLogin ? "_self" : "_blank"}
      >
        {loginLabel}
      </a>
    );
  };

  const handleDismissClick = (id: number, lastUpdatedOn: string) => () => {
    const messagesDismissed = getMsgCookie();
    messagesDismissed.push({ id, lastUpdatedOn });
    // Create a new list of messages exlcuding all the dismissed ones
    const newMessages = allMessages.filter((msg: any) => !messagesDismissed.find((m) => m?.id === msg?.id)).slice(0, 2);
    setMessages(newMessages);
    updateMsgCookie(messagesDismissed);
    return false;
  };

  const renderLoginTray = (cn?: string) => {
    return (
      <ul className={cx("messages__item-container", cn)}>
        {messages.map((item) => {
          const { id, lastUpdatedOn } = item;
          return (
            <Message
              {...item}
              onDismissClick={handleDismissClick(id, lastUpdatedOn)}
              className="messages__item"
            />
          );
        })}
      </ul>
    );
  };

  const renderDropdownWithLogin = () => {
    return (
      <div className={cx("faux-dropdown", dropdownClass)}>
        <button className="faux-dropdown__trigger faux-dropdown__trigger--cta js-faux-dropdown">{loginLabel}</button>
        <div
          className={cx("faux-dropdown__menu faux-dropdown__menu--no-cursor js-faux-dropdown-menu", {
            "faux-dropdown__menu--in-hat faux-dropdown__menu--drop-to-right": !inNav,
          })}
          data-disable-auto-close="true"
        >
          {!!messages.length && <div className="messages__section--padded border-bottom">{renderLoginTray()}</div>}
          <div className="messages__section messages__section--border-top messages__cta">
            <div className="messages__section--padded">{renderLinks()}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderNavLogin = () => {
    return (
      <Fragment>
        {!!messages.length && (
          <div className="nav__mobile-section--padded nav__mobile-section--white">
            {renderLoginTray("nav__mobile-section--padded-top")}
          </div>
        )}
        <div className="nav__mobile-section--padded">{renderLinks()}</div>
      </Fragment>
    );
  };

  // There are messages to show or this role + region has multiple login portals to choose from. Render login area.
  if (messages.length || multipleLogin) {
    return <Fragment>{inNav ? renderNavLogin() : renderDropdownWithLogin()}</Fragment>;
  } else {
    // Otherwise, if all messages have been dismissed, render a simple button directly to the login portal
    return renderSingleLoginButton();
  }
};

export default App;
